import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { FileText, Shield, HelpCircle, Send } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

export default function LegalInfoPage() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('terms');

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['terms', 'privacy', 'pqrs'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const [pqrsForm, setPqrsForm] = useState({
    name: '',
    email: '',
    phone: '',
    type: 'peticion', // peticion | queja | reclamo | sugerencia
    description: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handlePqrsSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setTimeout(() => {
      toast.success('Tu solicitud de PQRS ha sido enviada exitosamente. Nos comunicaremos contigo pronto.');
      setPqrsForm({ name: '', email: '', phone: '', type: 'peticion', description: '' });
      setSubmitting(false);
    }, 1000);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 animate-fade-in" data-testid="legal-page">
      <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#46371c] mb-8 text-center">Información Legal y PQRS</h1>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        {/* Navigation Sidebar */}
        <div className="md:col-span-1 space-y-2">
          <button
            onClick={() => setActiveTab('terms')}
            className={`w-full text-left px-4 py-3 rounded-xl font-body text-sm font-medium flex items-center gap-2 transition-all ${
              activeTab === 'terms' ? 'bg-[#46371c] text-[#fcfbf9]' : 'bg-[#f4f1ea] text-[#75664d] hover:bg-[#e5dfd3]'
            }`}
          >
            <FileText className="w-4 h-4" /> Términos y Condiciones
          </button>
          <button
            onClick={() => setActiveTab('privacy')}
            className={`w-full text-left px-4 py-3 rounded-xl font-body text-sm font-medium flex items-center gap-2 transition-all ${
              activeTab === 'privacy' ? 'bg-[#46371c] text-[#fcfbf9]' : 'bg-[#f4f1ea] text-[#75664d] hover:bg-[#e5dfd3]'
            }`}
          >
            <Shield className="w-4 h-4" /> Políticas de Privacidad
          </button>
          <button
            onClick={() => setActiveTab('pqrs')}
            className={`w-full text-left px-4 py-3 rounded-xl font-body text-sm font-medium flex items-center gap-2 transition-all ${
              activeTab === 'pqrs' ? 'bg-[#46371c] text-[#fcfbf9]' : 'bg-[#f4f1ea] text-[#75664d] hover:bg-[#e5dfd3]'
            }`}
          >
            <HelpCircle className="w-4 h-4" /> Radicar PQRS
          </button>
        </div>

        {/* Content Area */}
        <div className="md:col-span-3">
          <Card className="border border-[#e5dfd3] bg-[#fcfbf9] rounded-2xl overflow-hidden shadow-sm">
            <CardContent className="p-6 sm:p-8">
              {activeTab === 'terms' && (
                <div className="prose prose-stone max-w-none font-body text-sm text-[#75664d] space-y-4">
                  <h2 className="font-heading text-xl font-bold text-[#46371c] border-b border-[#e5dfd3] pb-2">Términos y Condiciones de Uso</h2>
                  <p>Bienvenido a Keiks Repostería. Al acceder y realizar pedidos a través de nuestro sitio web, usted acepta cumplir y estar sujeto a los siguientes términos y condiciones de uso:</p>
                  
                  <h3 className="font-heading font-semibold text-[#46371c] mt-4">1. Pedidos y Confirmación</h3>
                  <p>Todos los pedidos personalizados requieren un tiempo mínimo de preparación especificado en el producto. El pedido solo se considerará confirmado una vez se realice y verifique el depósito mínimo requerido del 50% del total.</p>
                  
                  <h3 className="font-heading font-semibold text-[#46371c] mt-4">2. Políticas de Cancelación y Reembolso</h3>
                  <p>Las cancelaciones o modificaciones de pedidos deben realizarse con al menos 24 horas de anticipación a la fecha de entrega acordada. Pasado este plazo, el valor del depósito no será reembolsable debido a la compra previa de insumos e inicio de preparación artesanal.</p>
                  
                  <h3 className="font-heading font-semibold text-[#46371c] mt-4">3. Entregas y Retiros</h3>
                  <p>Es responsabilidad del cliente proporcionar una dirección de envío correcta y asegurar que haya alguien disponible para recibir el pedido en la franja horaria programada. En caso de retiro en tienda física, el pedido se retendrá únicamente durante el horario laboral del día programado.</p>
                  
                  <h3 className="font-heading font-semibold text-[#46371c] mt-4">4. Variaciones del Producto</h3>
                  <p>Al ser productos de repostería artesanal elaborados a mano, las decoraciones finales, colores y detalles estéticos pueden presentar variaciones menores respecto a las fotos de referencia del catálogo.</p>
                </div>
              )}

              {activeTab === 'privacy' && (
                <div className="prose prose-stone max-w-none font-body text-sm text-[#75664d] space-y-4">
                  <h2 className="font-heading text-xl font-bold text-[#46371c] border-b border-[#e5dfd3] pb-2">Políticas de Privacidad y Tratamiento de Datos Personal</h2>
                  <p>De conformidad con la Ley 1581 de 2012 de Protección de Datos Personales en Colombia, en Keiks Repostería nos comprometemos a garantizar la confidencialidad y seguridad de su información personal:</p>
                  
                  <h3 className="font-heading font-semibold text-[#46371c] mt-4">1. Datos Recolectados</h3>
                  <p>Recopilamos información básica de contacto como su nombre completo, dirección de entrega, correo electrónico y número de teléfono celular, necesarios exclusivamente para procesar, programar y entregar sus pedidos de repostería.</p>
                  
                  <h3 className="font-heading font-semibold text-[#46371c] mt-4">2. Uso de la Información</h3>
                  <p>Sus datos personales son utilizados para la coordinación logística de los envíos, notificaciones de estado del pedido por correo o WhatsApp, y resolución de incidencias en la facturación.</p>
                  
                  <h3 className="font-heading font-semibold text-[#46371c] mt-4">3. Transferencia a Terceros</h3>
                  <p>Keiks Repostería no vende ni alquila sus datos a terceros. Únicamente compartimos su dirección y teléfono con transportistas autorizados encargados de realizar la entrega final de su producto.</p>
                  
                  <h3 className="font-heading font-semibold text-[#46371c] mt-4">4. Derechos del Titular</h3>
                  <p>Como titular de sus datos personales, usted tiene derecho a conocer, actualizar, rectificar y solicitar la eliminación de su información de nuestras bases de datos en cualquier momento enviando una solicitud a través de nuestra sección de PQRS o a nuestro correo electrónico oficial.</p>
                </div>
              )}

              {activeTab === 'pqrs' && (
                <div>
                  <h2 className="font-heading text-xl font-bold text-[#46371c] border-b border-[#e5dfd3] pb-2 mb-6">Radicación de PQRS (Peticiones, Quejas, Reclamos y Sugerencias)</h2>
                  <p className="font-body text-sm text-[#75664d] mb-6">Queremos brindarte la mejor experiencia posible. Si has tenido algún inconveniente o deseas hacernos una sugerencia, por favor diligencia el siguiente formulario formal:</p>
                  
                  <form onSubmit={handlePqrsSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="font-body text-xs text-[#75664d] mb-1 block font-medium">Nombre Completo *</label>
                        <input
                          type="text"
                          required
                          value={pqrsForm.name}
                          onChange={e => setPqrsForm(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                        />
                      </div>
                      <div>
                        <label className="font-body text-xs text-[#75664d] mb-1 block font-medium">Correo Electrónico *</label>
                        <input
                          type="email"
                          required
                          value={pqrsForm.email}
                          onChange={e => setPqrsForm(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="font-body text-xs text-[#75664d] mb-1 block font-medium">Número de Teléfono</label>
                        <input
                          type="tel"
                          value={pqrsForm.phone}
                          onChange={e => setPqrsForm(prev => ({ ...prev, phone: e.target.value }))}
                          className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                        />
                      </div>
                      <div>
                        <label className="font-body text-xs text-[#75664d] mb-1 block font-medium">Tipo de Solicitud *</label>
                        <select
                          value={pqrsForm.type}
                          onChange={e => setPqrsForm(prev => ({ ...prev, type: e.target.value }))}
                          className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none bg-white focus:border-[#a0d5ba]"
                        >
                          <option value="peticion">Petición</option>
                          <option value="queja">Queja</option>
                          <option value="reclamo">Reclamo</option>
                          <option value="sugerencia">Sugerencia</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="font-body text-xs text-[#75664d] mb-1 block font-medium">Detalle / Descripción de la Solicitud *</label>
                      <textarea
                        required
                        rows={4}
                        value={pqrsForm.description}
                        onChange={e => setPqrsForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Describe detalladamente los hechos, fechas u observaciones correspondientes..."
                        className="w-full p-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba] resize-none"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="bg-[#46371c] hover:bg-[#2d2312] text-[#fcfbf9] rounded-full w-full sm:w-auto px-6 font-body text-sm"
                    >
                      <Send className="w-4 h-4 mr-2" /> {submitting ? 'Enviando...' : 'Radicar PQRS'}
                    </Button>
                  </form>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
